/**
 * iter63z''''''' — Tenant Branding tab on Site Admin.
 *
 * Lets the tenant admin set the tenant's logo URL, app name, tagline,
 * primary + secondary colors, and support email. Shows a live preview
 * pane next to the form so the admin sees their changes immediately
 * before saving. Saving PUTs to /api/tenant-admin/branding; on success
 * we also force a global theming refresh by dispatching a "tcr:branding-updated"
 * window event that the app-boot listener (App.jsx) picks up to re-apply
 * CSS variables without a page reload.
 *
 * v1 scope: URL field for logo (no file upload), hex input for colors
 * (no presets), no platform-admin tenant list, no custom domain. Those
 * land in roadmap items as the product matures.
 */
import { useEffect, useState } from "react";
import { Save, Eye, Palette, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";

export default function TenantBrandingTab() {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [tenant, setTenant] = useState(null);
    const [form, setForm] = useState({
        logo_url: "",
        app_name: "",
        tagline: "",
        primary_color: "#CCFF00",
        secondary_color: "#00E5FF",
        support_email: "",
    });

    useEffect(() => {
        (async () => {
            try {
                const r = await api.getTenantMe();
                setTenant(r);
                setForm({
                    logo_url: r.branding?.logo_url || "",
                    app_name: r.branding?.app_name || "",
                    tagline: r.branding?.tagline || "",
                    primary_color: r.branding?.primary_color || "#CCFF00",
                    secondary_color: r.branding?.secondary_color || "#00E5FF",
                    support_email: r.branding?.support_email || "",
                });
            } catch (e) {
                toast.error("Failed to load tenant branding");
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    const update = (k, v) => setForm(f => ({ ...f, [k]: v }));

    const save = async () => {
        setSaving(true);
        try {
            const r = await api.updateBranding(form);
            toast.success("Branding saved");
            setTenant(t => ({ ...t, branding: r.branding }));
            // Tell the rest of the app to re-apply CSS variables now.
            window.dispatchEvent(new CustomEvent("tcr:branding-updated", { detail: r.branding }));
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    const resetToDefaults = () => {
        setForm({
            logo_url: "",
            app_name: "",
            tagline: "",
            primary_color: "#CCFF00",
            secondary_color: "#00E5FF",
            support_email: "",
        });
    };

    if (loading) {
        return <p className="text-zinc-500 text-sm" data-testid="branding-tab-loading">Loading branding…</p>;
    }

    return (
        <div className="grid lg:grid-cols-2 gap-6" data-testid="tenant-branding-tab">
            {/* Form */}
            <div className="brutal-card p-5 space-y-4">
                <div className="flex items-center gap-2 mb-1">
                    <Palette className="w-4 h-4 text-volt" />
                    <h3 className="font-display font-extrabold text-base uppercase tracking-tight">
                        Tenant branding
                    </h3>
                </div>
                <p className="text-xs text-zinc-500">
                    Set the logo, colors, and copy your players see across the app. Empty fields fall back to the platform defaults.
                </p>

                <Field label="App name" hint="Shown in the header + browser title.">
                    <input
                        data-testid="branding-app-name-input"
                        className="brutal-input w-full"
                        value={form.app_name}
                        onChange={e => update("app_name", e.target.value)}
                        placeholder="Tiny Crew Rivalries"
                        maxLength={80}
                    />
                </Field>

                <Field label="Tagline" hint="Short subtitle under the app name (optional).">
                    <input
                        data-testid="branding-tagline-input"
                        className="brutal-input w-full"
                        value={form.tagline}
                        onChange={e => update("tagline", e.target.value)}
                        placeholder="Pick'em rivalries for tiny crews"
                        maxLength={160}
                    />
                </Field>

                <Field label="Logo URL" hint="Full https URL to your logo image (recommended PNG/SVG, square).">
                    <input
                        data-testid="branding-logo-url-input"
                        className="brutal-input w-full"
                        value={form.logo_url}
                        onChange={e => update("logo_url", e.target.value)}
                        placeholder="https://your-cdn.com/logo.png"
                        maxLength={2048}
                    />
                </Field>

                <div className="grid grid-cols-2 gap-3">
                    <Field label="Primary color" hint="Hex (e.g. #CCFF00).">
                        <div className="flex items-center gap-2">
                            <input
                                type="color"
                                data-testid="branding-primary-color-picker"
                                value={form.primary_color}
                                onChange={e => update("primary_color", e.target.value.toUpperCase())}
                                className="w-10 h-10 cursor-pointer rounded-sm border-2 border-[#333]"
                            />
                            <input
                                data-testid="branding-primary-color-input"
                                className="brutal-input flex-1 font-mono text-xs"
                                value={form.primary_color}
                                onChange={e => update("primary_color", e.target.value.toUpperCase())}
                                placeholder="#CCFF00"
                                maxLength={9}
                            />
                        </div>
                    </Field>
                    <Field label="Secondary color" hint="Hex (accent + highlights).">
                        <div className="flex items-center gap-2">
                            <input
                                type="color"
                                data-testid="branding-secondary-color-picker"
                                value={form.secondary_color}
                                onChange={e => update("secondary_color", e.target.value.toUpperCase())}
                                className="w-10 h-10 cursor-pointer rounded-sm border-2 border-[#333]"
                            />
                            <input
                                data-testid="branding-secondary-color-input"
                                className="brutal-input flex-1 font-mono text-xs"
                                value={form.secondary_color}
                                onChange={e => update("secondary_color", e.target.value.toUpperCase())}
                                placeholder="#00E5FF"
                                maxLength={9}
                            />
                        </div>
                    </Field>
                </div>

                <Field label="Support email" hint="Where players reach out for help.">
                    <input
                        data-testid="branding-support-email-input"
                        className="brutal-input w-full"
                        value={form.support_email}
                        onChange={e => update("support_email", e.target.value)}
                        placeholder="hello@example.com"
                        maxLength={160}
                    />
                </Field>

                <div className="flex gap-2 pt-2">
                    <button
                        data-testid="branding-save-btn"
                        onClick={save}
                        disabled={saving}
                        className="brutal-btn bg-volt text-white text-xs py-2 flex items-center gap-2"
                    >
                        <Save className="w-3.5 h-3.5" /> {saving ? "Saving…" : "Save branding"}
                    </button>
                    <button
                        data-testid="branding-reset-btn"
                        onClick={resetToDefaults}
                        disabled={saving}
                        className="brutal-btn-ghost text-xs py-2 flex items-center gap-2"
                        title="Reset the form to platform defaults (doesn't save until you click Save)"
                    >
                        <RotateCcw className="w-3.5 h-3.5" /> Reset to defaults
                    </button>
                </div>
            </div>

            {/* Live preview */}
            <div className="brutal-card p-5 space-y-3" data-testid="branding-preview">
                <div className="flex items-center gap-2 mb-1">
                    <Eye className="w-4 h-4 text-cyber" />
                    <h3 className="font-display font-extrabold text-base uppercase tracking-tight">
                        Live preview
                    </h3>
                </div>
                <p className="text-xs text-zinc-500">
                    What crewmates will see across the app. Not saved until you click Save.
                </p>
                <div
                    className="brutal-card-light p-4 space-y-3"
                    style={{
                        borderColor: form.primary_color,
                    }}
                >
                    <div className="flex items-center gap-3">
                        {form.logo_url ? (
                            <img
                                src={form.logo_url}
                                alt="Logo preview"
                                onError={(e) => { e.target.style.display = "none"; }}
                                className="w-12 h-12 object-contain rounded-sm border-2 border-[#333]"
                                data-testid="branding-preview-logo"
                            />
                        ) : (
                            <div
                                className="w-12 h-12 rounded-sm flex items-center justify-center font-display font-black text-xl"
                                style={{
                                    backgroundColor: form.primary_color,
                                    color: "#0a0a0a",
                                }}
                                data-testid="branding-preview-logo-placeholder"
                            >
                                {(form.app_name || "T").charAt(0).toUpperCase()}
                            </div>
                        )}
                        <div>
                            <p className="font-display font-black text-lg leading-none" data-testid="branding-preview-app-name">
                                {form.app_name || "Tiny Crew Rivalries"}
                            </p>
                            {form.tagline && (
                                <p className="text-[11px] text-zinc-400 mt-1" data-testid="branding-preview-tagline">
                                    {form.tagline}
                                </p>
                            )}
                        </div>
                    </div>
                    <div className="flex gap-2">
                        <span
                            className="pill text-[10px]"
                            style={{
                                backgroundColor: form.primary_color,
                                color: "#0a0a0a",
                                borderColor: form.primary_color,
                            }}
                        >
                            Primary
                        </span>
                        <span
                            className="pill text-[10px]"
                            style={{
                                backgroundColor: form.secondary_color,
                                color: "#0a0a0a",
                                borderColor: form.secondary_color,
                            }}
                        >
                            Secondary
                        </span>
                    </div>
                    {form.support_email && (
                        <p className="text-[11px] text-zinc-500">
                            Support: <span className="text-zinc-300">{form.support_email}</span>
                        </p>
                    )}
                </div>
                {tenant?.branding?.configured ? (
                    <p className="text-[11px] text-zinc-500" data-testid="branding-status-configured">
                        Last updated {tenant.branding.updated_at ? new Date(tenant.branding.updated_at).toLocaleString() : "—"}.
                    </p>
                ) : (
                    <p className="text-[11px] text-volt" data-testid="branding-status-unconfigured">
                        Branding is not yet customized — your tenant still shows platform defaults.
                    </p>
                )}
            </div>
        </div>
    );
}

function Field({ label, hint, children }) {
    return (
        <label className="block space-y-1">
            <span className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</span>
            {children}
            {hint && <span className="block text-[11px] text-zinc-500">{hint}</span>}
        </label>
    );
}
